/**
 * Quotations list helpers — always via server REST API.
 * Mutations go through quotationsServer.ts.
 */

import { Quote } from '../models/quote';
import { authenticatedHttpClient } from './httpClient';
import {
  createApiListCursor,
  getApiListOffset,
} from '../utils/apiListCursor';

export type { Quote };

/** Opaque pagination cursor (API offset), kept compatible with list UI state. */
export type QuotesListCursor = { __apiOffset: number } | null;

export interface QuotesQueryResult {
  quotes: Quote[];
  lastDoc: QuotesListCursor;
  hasMore: boolean;
}

export const loadQuotes = async (
  organizationId: string,
  searchInput?: string,
  lastQuote?: QuotesListCursor | { __apiOffset?: number } | null,
  limitCount: number = 10
): Promise<QuotesQueryResult> => {
  const offset = getApiListOffset(lastQuote as { __apiOffset?: number } | null, 0);
  const params = new URLSearchParams();
  params.set('limit', String(limitCount));
  params.set('offset', String(offset));
  if (searchInput?.trim()) {
    params.set('search_term', searchInput.trim().toLowerCase());
  }
  const result = await authenticatedHttpClient.get<{
    quotes: Quote[];
    hasMore?: boolean;
  }>(`/quotes/${organizationId}?${params.toString()}`);
  const hasMore = result.hasMore ?? result.quotes.length >= limitCount;
  return {
    quotes: result.quotes,
    hasMore,
    lastDoc: hasMore ? createApiListCursor(offset + limitCount) : null,
  };
};
